import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography
} from '@mui/material';
import {
  NotificationsActive as NotificationsActiveIcon,
  NotificationsOff as NotificationsOffIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../redux/slices/authSlice';
import {
  disableWebPushForCurrentDevice,
  enableWebPushForCurrentDevice,
  getWebPushStatus
} from '../../services/webPushService';

const WebPushSettingsSection = () => {
  const user = useSelector(selectCurrentUser);
  const [webPushStatus, setWebPushStatus] = useState({
    supported: false,
    configured: false,
    permission: 'default',
    active: false
  });
  const [webPushLoading, setWebPushLoading] = useState(false);
  const [webPushError, setWebPushError] = useState('');
  const [webPushNotice, setWebPushNotice] = useState('');

  const refreshWebPushStatus = async () => {
    const status = await getWebPushStatus();
    setWebPushStatus(status);
  };

  useEffect(() => {
    let cancelled = false;

    const loadWebPushStatus = async () => {
      try {
        const status = await getWebPushStatus();
        if (!cancelled) {
          setWebPushStatus(status);
        }
      } catch (error) {
        if (!cancelled) {
          setWebPushStatus(prev => ({ ...prev, active: false }));
        }
      }
    };

    loadWebPushStatus();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleEnableWebPush = async () => {
    setWebPushLoading(true);
    setWebPushError('');
    setWebPushNotice('');

    try {
      await enableWebPushForCurrentDevice(user);
      await refreshWebPushStatus();
      setWebPushNotice('Push-Benachrichtigungen sind auf diesem Gerät aktiv.');
    } catch (error) {
      setWebPushError(error.message || 'Push konnte nicht aktiviert werden.');
      await refreshWebPushStatus().catch(() => {});
    } finally {
      setWebPushLoading(false);
    }
  };

  const handleDisableWebPush = async () => {
    setWebPushLoading(true);
    setWebPushError('');
    setWebPushNotice('');

    try {
      await disableWebPushForCurrentDevice();
      await refreshWebPushStatus();
      setWebPushNotice('Push-Benachrichtigungen wurden auf diesem Gerät deaktiviert.');
    } catch (error) {
      setWebPushError(error.message || 'Push konnte nicht deaktiviert werden.');
      await refreshWebPushStatus().catch(() => {});
    } finally {
      setWebPushLoading(false);
    }
  };

  const getWebPushHelperText = () => {
    if (!webPushStatus.supported) {
      return 'Dieser Browser unterstützt Web Push nicht. Auf iOS muss das Portal als Home-Screen-App geöffnet werden.';
    }

    if (!webPushStatus.configured) {
      return 'Der öffentliche Web-Push-Schlüssel ist noch nicht konfiguriert.';
    }

    if (webPushStatus.permission === 'denied') {
      return 'Benachrichtigungen sind im Browser oder Betriebssystem blockiert.';
    }

    if (webPushStatus.active) {
      return 'Dieses Gerät ist für Web-Push-Benachrichtigungen registriert.';
    }

    return 'Aktivieren Sie Push auf diesem Gerät, um auch bei geschlossener installierter Web-App informiert zu werden.';
  };

  return (
    <>
      <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Push-Benachrichtigungen
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Lokale Einstellung (nur dieses Gerät)
        </Typography>
      </Box>

      <List disablePadding>
        <ListItem>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={{ width: '100%' }}
          >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                {webPushStatus.active ? (
                  <NotificationsActiveIcon color="primary" />
                ) : (
                  <NotificationsOffIcon color="action" />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Push auf diesem Gerät"
                secondary={getWebPushHelperText()}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Chip
                label={webPushStatus.active ? 'Aktiv' : 'Inaktiv'}
                color={webPushStatus.active ? 'success' : 'default'}
                variant={webPushStatus.active ? 'filled' : 'outlined'}
              />
              {webPushStatus.active ? (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleDisableWebPush}
                  disabled={webPushLoading}
                  startIcon={webPushLoading ? <CircularProgress size={18} /> : <NotificationsOffIcon />}
                >
                  Deaktivieren
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleEnableWebPush}
                  disabled={webPushLoading || !webPushStatus.supported || !webPushStatus.configured || webPushStatus.permission === 'denied'}
                  startIcon={webPushLoading ? <CircularProgress size={18} color="inherit" /> : <NotificationsActiveIcon />}
                >
                  Aktivieren
                </Button>
              )}
            </Stack>
          </Stack>
        </ListItem>
      </List>

      {(webPushError || webPushNotice) && (
        <Box sx={{ px: 2, pb: 2 }}>
          {webPushError && (
            <Alert severity="error">
              {webPushError}
            </Alert>
          )}
          {webPushNotice && (
            <Alert severity="success">
              {webPushNotice}
            </Alert>
          )}
        </Box>
      )}
    </>
  );
};

export default WebPushSettingsSection;
